import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import ReactCountryFlag from "react-country-flag";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import {
  Box,
  BoxProps,
  InputAdornment,
  Typography,
  useTheme,
} from "@mui/material";
import { Region, useListAndSearchAllRegions } from "@rentablo/api/apiv1";

const OptionContainer = styled.div`
  display: grid;
  grid-template-columns: 16px auto;
  gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
`;

interface Props extends Omit<BoxProps, "onChange"> {
  defaultRegion?: Region;
  className?: string;
  onChange?: (region: Region | null) => void;
  isError?: boolean;
  errorText?: string;
  disabled?: boolean;
  variant?: "outlined" | "filled" | "standard";
  label?: string;
  testId?: string;
  noLabel?: boolean;
  includeGlobal?: boolean;
  placeholder?: string;
  showFlagOnInput?: boolean;
}

export const RegionSelector = ({
  className,
  onChange,
  defaultRegion,
  label,
  placeholder,
  isError = false,
  errorText,
  disabled = false,
  testId = "region-selector-input",
  noLabel = false,
  includeGlobal = false,
  showFlagOnInput = false,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data, isFetching } = useListAndSearchAllRegions(
    { type: "" },
    { staleTime: Infinity }
  );
  const [region, setRegion] = useState(defaultRegion ?? null);

  const handleChange = (value: Region | null) => {
    let region = value;
    if (includeGlobal && value === null) {
      region =
        data?.regions?.find((region) => region.germanName === "Global") || null;
    }
    onChange && onChange(region);
    setRegion(value);
  };

  const regions =
    data?.regions?.filter(
      (region) =>
        region.regionType === "country" ||
        (includeGlobal ? region.germanName === "Global" : false)
    ) || [];

  return (
    <Box {...props}>
      {noLabel ? null : (
        <Typography
          component="label"
          variant="body3"
          sx={{ display: "inline-block", marginBottom: theme.spacing(1) }}
        >
          {label || t("regionSelector.label")}
        </Typography>
      )}
      <Autocomplete
        disabled={disabled}
        defaultValue={defaultRegion || null}
        className={className}
        value={region}
        disablePortal
        onChange={(e, value) => handleChange(value)}
        loading={isFetching}
        options={regions}
        blurOnSelect
        getOptionLabel={(option) => option.germanName || option.name || ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            error={isError}
            helperText={errorText}
            placeholder={placeholder || label}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "data-testid": testId,
              },
              input: {
                ...params.InputProps,
                startAdornment: showFlagOnInput ? (
                  <>
                    {region?.twoLetterCode && (
                      <InputAdornment
                        position="start"
                        sx={{
                          ml: 3,
                          mr: "0px!important",
                        }}
                      >
                        <ReactCountryFlag
                          countryCode={region.twoLetterCode}
                          svg
                          title={region.twoLetterCode}
                          style={{
                            width: "20px",
                            height: "16px",
                            borderRadius: "40%",
                          }}
                        />
                      </InputAdornment>
                    )}
                    {params.InputProps?.startAdornment}
                  </>
                ) : (
                  params.InputProps?.startAdornment
                ),
              },
            }}
          />
        )}
        renderOption={(props, option, { inputValue }) => {
          const matches = match(option?.germanName || "", inputValue, {
            insideWords: true,
          });
          const parts = parse(option?.germanName || "", matches);

          return (
            <li {...props}>
              <OptionContainer data-testid="region-selector-option">
                {option.twoLetterCode && (
                  <ReactCountryFlag
                    countryCode={option.twoLetterCode}
                    svg
                    title={option.twoLetterCode}
                  />
                )}
                <Typography variant="body3">
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{
                        fontWeight: 500,
                        color: part.highlight
                          ? theme.palette.text.primary
                          : theme.palette.text.secondary,
                      }}
                    >
                      {part.text}
                    </span>
                  ))}
                </Typography>
              </OptionContainer>
            </li>
          );
        }}
      />
    </Box>
  );
};
